import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenCPQReport from "./HiddenCPQReport"; // ✅ Hidden PDF renderer
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── Interpretation → colour map (for Grand Total pill) ───────────────────────
const INTERP_COLOR = {
  Excellent:          { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" },
  "Very Good":        { bg: "#dcfce7", color: "#1f8f55", border: "#bbf7d0" },
  Suitable:           { bg: "#dbeafe", color: "#3f7cac", border: "#bfdbfe" },
  "Nearly There":     { bg: "#fef3c7", color: "#d88a1f", border: "#fde68a" },
  Developing:         { bg: "#fee2e2", color: "#c2413b", border: "#fecaca" },
  "Needs improvement":{ bg: "#fee2e2", color: "#c2413b", border: "#fecaca" },
};

// ── Per-dimension colours (sourced from CPQResult.jsx) ────────────────────────
// Listening: blue  | TPG: red    | CBA: amber
// N-Verbal:  green | Inf.Share: purple | Articulation: pink
const DIM_COLOR = {
  "Listening":    { bg: "#eff6ff", color: "#1e3a8a", border: "#bfdbfe" },
  "TPG":          { bg: "#fff1f2", color: "#7f1d1d", border: "#fecaca" },
  "CBA":          { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  "N-Verbal":     { bg: "#ecfdf5", color: "#064e3b", border: "#6ee7b7" },
  "Inf.Share":    { bg: "#f5f3ff", color: "#4c1d95", border: "#ddd6fe" },
  "Articulation": { bg: "#fdf2f8", color: "#9d174d", border: "#fbcfe8" },
};

// Dimension display order + short labels
const DIMENSIONS = [
  { key: "Listening",    label: "Listening"    },
  { key: "TPG",          label: "TPG"          },
  { key: "CBA",          label: "CBA"          },
  { key: "N-Verbal",     label: "N-Verbal"     },
  { key: "Inf.Share",    label: "Inf.Share"    },
  { key: "Articulation", label: "Articulation" },
];

export default function GlobalCPQResponses({ surveyId }) {
  const [cpqData, setCpqData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters — matching GlobalVAKResponses exactly
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");

  // ✅ PDF Generation State (same pattern as GlobalVAKResponses)
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchCPQResults();
  }, [surveyId]);

  const fetchCPQResults = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score/cpq/all`,
        { survey_id: surveyId }
      );
      setCpqData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch CPQ results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  const uniqueCompanies = [
    ...new Set(cpqData.map(u => (u?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(cpqData.map(u => (u?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = cpqData.filter((user) => {
    const name    = (user?.name       || "").toLowerCase();
    const email   = (user?.email      || "").toLowerCase();
    const company = (user?.company    || "").trim().toLowerCase();
    const dept    = (user?.department || "").trim().toLowerCase();

    const matchName    = name.includes(filterName.toLowerCase());
    const matchEmail   = email.includes(filterEmail.toLowerCase());
    const matchCompany = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept    = filterDept    === "" || dept    === filterDept.toLowerCase();
    
    
    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time || user?.submission_time);
      submissionDate.setHours(0, 0, 0, 0);

      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
     setFilterCompany("");
    setFilterDept("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const formattedData = {
      userInfo: {
        name:  user?.name  || "Participant",
        email: user?.email || "",
        phone: user?.phone || "",
      },
      scores: user.scores || {},
      submission_time: user.submission_time || user?.submission_time,
    };

    setDownloadData(formattedData);
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading CPQ data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>
      <SurveyInviteWidget surveyId={surveyId} />

      {/* ✅ Hidden PDF renderer — same pattern as HiddenVAKReport */}
      <HiddenCPQReport
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h3 style={{ margin: 0 }}>💬 CPQ Communication Proficiency ({filteredData.length})</h3>
        <span style={{ background: "#0f2744", color: "white", padding: "5px 10px", borderRadius: "4px", fontSize: "12px", fontWeight: "bold" }}>
          Global Public View
        </span>
      </div>

      {/* ── Filter Bar — identical layout to GlobalVAKResponses ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{ padding: "8px 15px", background: "#A81026", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {cpqData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No CPQ responses found.</div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>No responses match your current filters.</div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "220px" }}>
                  (Listening, TPG, CBA, N-Verbal, Inf.Share, Articulation)
                </th>
                <th style={{ padding: "12px", background: "#eafaf1" }}>📊 Grand Total</th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const grandTotal  = user.scores?.Grand_Total || { score: 0, out_of: 240, interpretation: "—" };
                const interpStyle = INTERP_COLOR[grandTotal.interpretation] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
                const isGenerating = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* Date */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time || user?.submission_time
                        ? <>
                            {new Date(user.submission_time || user?.submission_time).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time || user?.submission_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* Participant */}
                    <td style={{ padding: "12px" }}>
                      <b>{user?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user?.email}<br />
                        {user?.phone || ""}
                      </span>
                    </td>

                    {/* Company / Dept */}
                    <td style={{ padding: "12px" }}>
                      {user?.company
                        ? <><b>{user.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.department}</span>
                        : null
                      }
                    </td>

                    {/* Dimension score pills */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {DIMENSIONS.map(({ key, label }) => {
                          const dim = user.scores?.[key];
                          if (!dim) return null;
                          const c = DIM_COLOR[key] || { bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
                          return (
                            <span
                              key={key}
                              title={dim.interpretation}
                              style={{
                                background: c.bg,
                                color: c.color,
                                border: `1px solid ${c.border}`,
                                padding: "2px 7px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {label}: {dim.score}/{dim.out_of}
                            </span>
                          );
                        })}
                      </div>
                    </td>

                    {/* Grand Total */}
                    <td style={{ padding: "12px", background: "#fdfefe" }}>
                      <div style={{ marginBottom: "5px" }}>
                        <span style={{ fontSize: "20px", fontWeight: "800", color: "#2c3e50", letterSpacing: "-0.5px" }}>
                          {grandTotal.score}
                          <span style={{ fontSize: "13px", fontWeight: "500", color: "#7f8c8d" }}>
                            /{grandTotal.out_of}
                          </span>
                        </span>
                      </div>
                      <span
                        style={{
                          display: "inline-block",
                          background: interpStyle.bg,
                          color: interpStyle.color,
                          border: `1px solid ${interpStyle.border}`,
                          padding: "2px 10px",
                          borderRadius: "999px",
                          fontSize: "11px",
                          fontWeight: "700",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {grandTotal.interpretation}
                      </span>
                    </td>

                    {/* ✅ Download — triggers HiddenCPQReport, no routing */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating}
                        style={{
                          background: isGenerating ? "#95a5a6" : "#A81026",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          cursor: isGenerating ? "not-allowed" : "pointer",
                          fontWeight: "bold",
                          fontSize: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          margin: "0 auto",
                          transition: "background 0.3s",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}